/**
 * 按 tile 分批调度（DESIGN.md §4.4「按 tile 分批执行」）。
 *
 * 两件事，都是性能上的硬约束而非优化：
 *
 * 1. **只渲染看得见的块，外加一圈预取。** 8000×8000 文档在 lod 0 下有 1024 块，
 *    而 1080p 视口只看得到十几块。全量渲染不是慢一点，是慢两个数量级。
 *    预取那一圈是为了平移时不出现空白——用户拖动画布的速度远快于一帧的渲染时间。
 *
 * 2. **单次提交的 draw call 有上限。** M0-09 的尖刺测出每次 draw 的固定开销约
 *    0.26 ms，其中三分之二在建 uniform buffer 与 bind group 上；这也是 backend
 *    有 `drawBatch` 的原因。但一次提交上万个 draw 会把命令缓冲堆到驱动吃不消，
 *    并且整批要么全成要么全废、中途无法响应取消。所以按上限切批。
 *
 * 本模块是**纯计算**：不碰 GPU，不认识文档模型。tile 尺寸由调用方传入——
 * 分层规则不允许 gpu 层 import engine 的 `TILE` 常量，而把它变成参数
 * 反倒让调度可以在任意块大小下被单测。
 */
import type { RenderGraph } from './graph';

/** 块坐标。像素坐标一律不出现在本模块的公开接口里。 */
export interface TileCoord {
  readonly tx: number;
  readonly ty: number;
}

/** 视口在**文档像素**坐标系下的矩形。 */
export interface Viewport {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface DocumentSize {
  readonly width: number;
  readonly height: number;
}

export interface ScheduleOptions {
  readonly viewport: Viewport;
  readonly documentSize: DocumentSize;
  /** 块边长（像素）。由调用方提供，见文件头。 */
  readonly tileSize: number;
  /**
   * 视口外额外渲染多少圈块。默认 1。
   *
   * 0 会让平移时边缘闪白；调大则每帧成本按 (2r+n)² 增长，收益却只在快速平移时体现。
   */
  readonly prefetchRings?: number;
  /**
   * 单次提交允许的最大 draw call 数。默认 256。
   *
   * 不是越大越好：一批越大，取消的粒度越粗，掉帧时的卡顿越明显。
   */
  readonly maxDrawsPerSubmit?: number;
}

export const DEFAULT_PREFETCH_RINGS = 1;
export const DEFAULT_MAX_DRAWS_PER_SUBMIT = 256;

/** 一次提交。`draws` 是这一批要提交的 draw call 总数。 */
export interface TileBatch {
  readonly tiles: readonly TileCoord[];
  readonly draws: number;
}

export interface Schedule {
  /** 视口内的块（不含预取圈）。 */
  readonly visible: readonly TileCoord[];
  /** 视口内 + 预取圈，且已裁到文档范围内。这就是本帧真正要渲染的块。 */
  readonly tiles: readonly TileCoord[];
  readonly batches: readonly TileBatch[];
  readonly drawsPerTile: number;
  readonly totalDraws: number;
}

/** 块范围（闭区间）。空范围用 `cols`/`rows` 为 0 表示。 */
interface TileRange {
  readonly firstX: number;
  readonly firstY: number;
  readonly cols: number;
  readonly rows: number;
}

/**
 * 视口覆盖到的块范围，向外扩 `rings` 圈，再裁到文档网格。
 *
 * 先扩后裁，不能反过来：先裁掉再扩会把预取圈扩到文档外面去，
 * 得到一堆不存在的块。
 */
function rangeFor(options: ScheduleOptions, rings: number): TileRange {
  const { viewport, documentSize, tileSize } = options;
  if (tileSize <= 0 || !Number.isInteger(tileSize)) {
    throw new RangeError(`tileSize 必须是正整数，收到 ${tileSize}。`);
  }
  const gridCols = Math.max(0, Math.ceil(documentSize.width / tileSize));
  const gridRows = Math.max(0, Math.ceil(documentSize.height / tileSize));
  if (viewport.width <= 0 || viewport.height <= 0 || gridCols === 0 || gridRows === 0) {
    return { firstX: 0, firstY: 0, cols: 0, rows: 0 };
  }

  // 右下角取闭区间的最后一个像素：视口右边界恰好落在块边界上时不该多算一列
  const first = {
    x: Math.floor(viewport.x / tileSize) - rings,
    y: Math.floor(viewport.y / tileSize) - rings,
  };
  const last = {
    x: Math.floor((viewport.x + viewport.width - 1) / tileSize) + rings,
    y: Math.floor((viewport.y + viewport.height - 1) / tileSize) + rings,
  };

  const firstX = Math.max(0, first.x);
  const firstY = Math.max(0, first.y);
  const lastX = Math.min(gridCols - 1, last.x);
  const lastY = Math.min(gridRows - 1, last.y);

  return {
    firstX,
    firstY,
    cols: Math.max(0, lastX - firstX + 1),
    rows: Math.max(0, lastY - firstY + 1),
  };
}

function expand(range: TileRange): TileCoord[] {
  const tiles: TileCoord[] = [];
  for (let ty = range.firstY; ty < range.firstY + range.rows; ty++) {
    for (let tx = range.firstX; tx < range.firstX + range.cols; tx++) {
      tiles.push({ tx, ty });
    }
  }
  return tiles;
}

/**
 * 排出一帧的渲染计划。
 *
 * 不提交任何 GPU 命令——返回的批次交给调用方逐批 `drawBatch`。分开是为了
 * 让"提交了多少批、每批多少 draw、渲染了哪些块"全都可以在没有 GPU 的环境下断言。
 */
export function scheduleTiles(graph: RenderGraph, options: ScheduleOptions): Schedule {
  const rings = options.prefetchRings ?? DEFAULT_PREFETCH_RINGS;
  const maxDraws = options.maxDrawsPerSubmit ?? DEFAULT_MAX_DRAWS_PER_SUBMIT;
  if (rings < 0 || !Number.isInteger(rings)) {
    throw new RangeError(`prefetchRings 必须是非负整数，收到 ${rings}。`);
  }
  if (maxDraws < 1 || !Number.isInteger(maxDraws)) {
    throw new RangeError(`maxDrawsPerSubmit 必须是正整数，收到 ${maxDraws}。`);
  }

  // 排序前先校验图：环状依赖在这里就要炸，不能等到提交时
  graph.topologicalOrder();
  const drawsPerTile = graph.drawsPerTile;

  const visible = expand(rangeFor(options, 0));
  const tiles = expand(rangeFor(options, rings));

  if (drawsPerTile > maxDraws) {
    throw new RangeError(
      `单块需要 ${drawsPerTile} 次 draw，超过了单批上限 ${maxDraws}——` +
        `一块不能被拆到两批里（中间状态在 GPU 上不完整），请调大 maxDrawsPerSubmit。`,
    );
  }

  const batches: TileBatch[] = [];
  if (drawsPerTile > 0) {
    // 一块的所有 draw 必须落在同一批：它们共享中间纹理，拆开提交会读到半成品
    const tilesPerBatch = Math.floor(maxDraws / drawsPerTile);
    if (tilesPerBatch < 1) {
      // 上面的守卫已经拦掉了这种情况。留这道防线是因为它的失败模式很糟：
      // 步长为 0 的循环不会报错，它会挂死整个 worker，查起来比一条异常难得多。
      throw new RangeError(
        `每批装不下一块（单块 ${drawsPerTile} 次 draw，上限 ${maxDraws}）——调度无法进行。`,
      );
    }
    for (let i = 0; i < tiles.length; i += tilesPerBatch) {
      const slice = tiles.slice(i, i + tilesPerBatch);
      batches.push({ tiles: slice, draws: slice.length * drawsPerTile });
    }
  }

  return {
    visible,
    tiles,
    batches,
    drawsPerTile,
    totalDraws: tiles.length * drawsPerTile,
  };
}
